// Catalog.ID Client — The `cid/bitcash-instruments` document
// ============================================================================
// The receive addresses (FUND) and tokens (AUTH) this account created on the
// Bitcash v2 ledger (apps/bitcash/docs/plans/bitcash-v2.md M12, §4.10), as one document in the
// account's encrypted blob store, beside `cid/bitcash-wallets`.
//
// ── why it exists ───────────────────────────────────────────────────────────
// The v2 ledger stores no code and returns none (T2): `GET /api/wallet/tokens`
// and `/addresses` answer refs. The pilot listed codes and labels, so the
// client could "adopt" its default token by label; v2 cannot. Whoever will
// need a code again keeps it, and keeps it BEFORE the call that creates it on
// the ledger: a crash between the two then retries with the same code, which
// the ledger answers idempotently, instead of minting a second token (which
// costs a fee) or binding a second address.
//
// ── keys ────────────────────────────────────────────────────────────────────
// Each wallet is keyed by its REF (64 hex, `walletRefOf`), never by its
// spending code, so the document repeats no secret the wallet list holds, and
// an organization wallet's instruments (minted by this delegate) sit beside
// the personal ones. There is no organization-scoped store (apps/bitcash/docs/plans/bitcash-v2.md
// M12, the D4 ruling: deferred): the org wallet's receive address code stays in
// the founding delegate's document — other delegates see its ref only — and
// each entitled delegate (owner, treasurer) issues its OWN default token on the
// org wallet when it needs one, recorded here: revocable on its own, a fee
// after the wallet's first free token, one of its 128 permission slots. A token's labels (`catalog:default`, `catalog:<module>`)
// live only here: the ledger has no labels.
//
// A CID-only document: the bitcash manager's wire contract
// (`cid/bitcash-wallets`, `bitcash-wallets-release`) is untouched.
//
// DOM-free and side-effect-free; the reads and writes are
// `state/bitcash-instruments-store.ts`.

import { TYPE_AUTH, TYPE_FUND, compactToDisplay, extractType, toCompact } from './bitcash';

/** The blob's name inside the vault's reserved `cid` namespace. LOAD-BEARING (it is in the key derivation). */
export const BITCASH_INSTRUMENTS_BLOB = 'bitcash-instruments';

export const BITCASH_INSTRUMENTS_DOCUMENT_VERSION = 1;

export interface InstrumentFund {
    /** Display form, `FUND-…`. Public by nature: it is printed and may be published. */
    code: string;
    /** Unix seconds. */
    createdAt: number;
}

/** What a token is for: the account's default, a module's own, or an organization delegate's. */
export type TokenPurpose = 'default' | 'module' | 'delegate';

export interface InstrumentToken {
    /** Display form, `AUTH-…`. A bearer credential: it pays. */
    code: string;
    purpose: TokenPurpose;
    label: string;
    /** The receive address the token is bound to (D52), or null for an unbound one. */
    payee: string | null;
    /** Unix seconds. */
    createdAt: number;
}

export interface WalletInstruments {
    funds: InstrumentFund[];
    tokens: InstrumentToken[];
}

export interface BitcashInstrumentsDocument {
    v: 1;
    /** By wallet ref. */
    wallets: Record<string, WalletInstruments>;
}

const HEX64 = /^[0-9a-f]{64}$/;
const PURPOSES: readonly TokenPurpose[] = ['default', 'module', 'delegate'];

export function emptyInstrumentsDocument(): BitcashInstrumentsDocument {
    return { v: 1, wallets: {} };
}

function codeOf(value: unknown, type: typeof TYPE_FUND | typeof TYPE_AUTH, what: string): string {
    if (typeof value !== 'string' || extractType(value) !== type) throw new Error(`Your bitcash instruments are unreadable: ${what}`);
    return compactToDisplay(type, toCompact(value));
}

function secondsOf(value: unknown, what: string): number {
    if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0) throw new Error(`Your bitcash instruments are unreadable: ${what}`);
    return value;
}

/** Validate a decrypted blob and return the canonical shape (codes in display form). Throws on anything else. */
export function decodeInstrumentsDocument(value: unknown): BitcashInstrumentsDocument {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) throw new Error('Your bitcash instruments are unreadable');
    const o = value as Record<string, unknown>;
    if (o.v !== BITCASH_INSTRUMENTS_DOCUMENT_VERSION) throw new Error('Your bitcash instruments are of an unknown version');
    if (typeof o.wallets !== 'object' || o.wallets === null || Array.isArray(o.wallets)) throw new Error('Your bitcash instruments are unreadable');
    const wallets: Record<string, WalletInstruments> = {};
    for (const [ref, raw] of Object.entries(o.wallets as Record<string, unknown>)) {
        if (!HEX64.test(ref)) throw new Error('Your bitcash instruments are unreadable: a wallet ref');
        const w = (raw ?? {}) as Record<string, unknown>;
        if (!Array.isArray(w.funds) || !Array.isArray(w.tokens)) throw new Error('Your bitcash instruments are unreadable');
        wallets[ref] = {
            funds: w.funds.map((f: Record<string, unknown>) => ({ code: codeOf(f?.code, TYPE_FUND, 'an address'), createdAt: secondsOf(f?.createdAt, 'a date') })),
            tokens: w.tokens.map((t: Record<string, unknown>) => {
                if (!PURPOSES.includes(t?.purpose as TokenPurpose)) throw new Error('Your bitcash instruments are unreadable: a token\'s purpose');
                if (typeof t.label !== 'string' || t.label.length > 200) throw new Error('Your bitcash instruments are unreadable: a token\'s label');
                return {
                    code: codeOf(t.code, TYPE_AUTH, 'a token'),
                    purpose: t.purpose as TokenPurpose,
                    label: t.label,
                    payee: t.payee === null ? null : codeOf(t.payee, TYPE_FUND, 'a token\'s payee'),
                    createdAt: secondsOf(t.createdAt, 'a date'),
                };
            }),
        };
    }
    return { v: 1, wallets };
}

/** The instruments of one wallet (empty when there are none). */
export function instrumentsOf(doc: BitcashInstrumentsDocument, walletRef: string): WalletInstruments {
    return doc.wallets[walletRef] ?? { funds: [], tokens: [] };
}

/** The wallet's latest default token, or null. */
export function defaultTokenOf(doc: BitcashInstrumentsDocument, walletRef: string): InstrumentToken | null {
    const defaults = instrumentsOf(doc, walletRef).tokens.filter(t => t.purpose === 'default');
    return defaults.length === 0 ? null : defaults[defaults.length - 1];
}

/** The document with `code` among the wallet's receive addresses; unchanged when it already is (a retry). */
export function withFund(doc: BitcashInstrumentsDocument, walletRef: string, code: string, createdAt: number): BitcashInstrumentsDocument {
    const display = codeOf(code, TYPE_FUND, 'an address');
    const current = instrumentsOf(doc, walletRef);
    if (current.funds.some(f => f.code === display)) return doc;
    return { v: 1, wallets: { ...doc.wallets, [walletRef]: { ...current, funds: [...current.funds, { code: display, createdAt }] } } };
}

/** The document with the token among the wallet's; unchanged when its code already is (a retry). */
export function withToken(doc: BitcashInstrumentsDocument, walletRef: string, token: InstrumentToken): BitcashInstrumentsDocument {
    const display = codeOf(token.code, TYPE_AUTH, 'a token');
    const current = instrumentsOf(doc, walletRef);
    if (current.tokens.some(t => t.code === display)) return doc;
    const entry: InstrumentToken = { ...token, code: display, payee: token.payee === null ? null : codeOf(token.payee, TYPE_FUND, 'a payee') };
    return { v: 1, wallets: { ...doc.wallets, [walletRef]: { ...current, tokens: [...current.tokens, entry] } } };
}
